import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileDown, AlertTriangle, Filter, TrendingUp, Package, ArrowLeftRight, ShoppingCart, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('shortage');
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ locations: [], products: [], materials: [] });

  // Existing reports data
  const [pendingOrders, setPendingOrders] = useState([]);
  const [finishedGoods, setFinishedGoods] = useState([]);
  const [shortageReport, setShortageReport] = useState([]);

  // New reports data
  const [productionPerformance, setProductionPerformance] = useState({ summary: {}, items: [] });
  const [purchaseHistory, setPurchaseHistory] = useState({ summary: {}, items: [] });
  const [inventoryMovement, setInventoryMovement] = useState({ summary: {}, items: [] });

  // Filters
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: 'all',
    productId: '',
    materialId: '',
    location: '',
    transactionType: 'all'
  });

  useEffect(() => {
    fetchFilterOptions();
    fetchReports();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const res = await axios.get(`${API}/reports/filter-options`);
      setFilterOptions(res.data);
    } catch (error) {
      console.error('Failed to fetch filter options');
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [pendingRes, goodsRes, shortageRes] = await Promise.all([
        axios.get(`${API}/reports/pending-orders`),
        axios.get(`${API}/reports/finished-goods-balance`),
        axios.get(`${API}/reports/raw-material-shortage`),
      ]);
      setPendingOrders(pendingRes.data);
      setFinishedGoods(goodsRes.data);
      setShortageReport(shortageRes.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductionPerformance = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.fromDate) params.append('from_date', filters.fromDate);
      if (filters.toDate) params.append('to_date', filters.toDate);
      if (filters.productId) params.append('product_id', filters.productId);
      if (filters.status !== 'all') params.append('status', filters.status);

      const res = await axios.get(`${API}/reports/production-performance?${params}`);
      setProductionPerformance(res.data);
    } catch (error) {
      toast.error('Failed to fetch production performance report');
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseHistory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.fromDate) params.append('from_date', filters.fromDate);
      if (filters.toDate) params.append('to_date', filters.toDate);
      if (filters.materialId) params.append('material_id', filters.materialId);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.location) params.append('location', filters.location);

      const res = await axios.get(`${API}/reports/purchase-order-history?${params}`);
      setPurchaseHistory(res.data);
    } catch (error) {
      toast.error('Failed to fetch purchase history report');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryMovement = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.fromDate) params.append('from_date', filters.fromDate);
      if (filters.toDate) params.append('to_date', filters.toDate);
      if (filters.materialId) params.append('material_id', filters.materialId);
      if (filters.transactionType !== 'all') params.append('transaction_type', filters.transactionType);
      if (filters.location) params.append('location', filters.location);

      const res = await axios.get(`${API}/reports/inventory-movement?${params}`);
      setInventoryMovement(res.data);
    } catch (error) {
      toast.error('Failed to fetch inventory movement report');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    if (activeTab === 'production') fetchProductionPerformance();
    else if (activeTab === 'purchase') fetchPurchaseHistory();
    else if (activeTab === 'inventory') fetchInventoryMovement();
    else fetchReports();
  };

  const clearFilters = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      status: 'all',
      productId: '',
      materialId: '',
      location: '',
      transactionType: 'all'
    });
  };

  // Export functions
  const exportPDF = (type) => {
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString();

    if (type === 'pending') {
      doc.text('Pending Orders Report', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated: ${today}`, 14, 22);
      doc.autoTable({
        startY: 28,
        head: [['SO Number', 'Customer', 'Product', 'Ordered', 'Supplied', 'Pending']],
        body: pendingOrders.map(order => [
          order.so_number, order.customer, order.product_name,
          order.ordered_qty, order.supplied_qty, order.pending_qty,
        ]),
      });
    } else if (type === 'goods') {
      doc.text('Finished Goods Balance Report', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated: ${today}`, 14, 22);
      doc.autoTable({
        startY: 28,
        head: [['Product Code', 'Name', 'Stock', 'Unit']],
        body: finishedGoods.map(good => [good.product_code, good.name, good.current_stock, good.unit]),
      });
    } else if (type === 'shortage') {
      doc.text('Raw Material Shortage Report', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated: ${today}`, 14, 22);
      doc.autoTable({
        startY: 28,
        head: [['Code', 'Material', 'Required', 'Available', 'Shortage', 'Status']],
        body: shortageReport.map(item => [
          item.material_code, item.material_name, item.required_qty.toFixed(2),
          item.available_qty.toFixed(2), item.shortage_qty.toFixed(2), item.status,
        ]),
      });
    } else if (type === 'production') {
      doc.text('Production Performance Report', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated: ${today} | Completion Rate: ${productionPerformance.summary.completion_rate}%`, 14, 22);
      doc.autoTable({
        startY: 28,
        head: [['Type', 'Reference', 'Product', 'Qty', 'Stage', 'Status']],
        body: productionPerformance.items.map(item => [
          item.type, item.reference, item.product_name, item.quantity, item.current_stage, item.status,
        ]),
      });
    } else if (type === 'purchase') {
      doc.text('Purchase Order History Report', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated: ${today} | Total Value: ${purchaseHistory.summary.total_value?.toLocaleString()}`, 14, 22);
      doc.autoTable({
        startY: 28,
        head: [['PO Number', 'Date', 'Supplier', 'Material', 'Qty', 'Price', 'Total', 'Status']],
        body: purchaseHistory.items.map(item => [
          item.po_number, item.date, item.supplier, item.material_name,
          item.quantity, item.unit_price, item.total.toFixed(2), item.status,
        ]),
      });
    } else if (type === 'inventory') {
      doc.text('Inventory Movement Report', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated: ${today} | Net Change: ${inventoryMovement.summary.net_change}`, 14, 22);
      doc.autoTable({
        startY: 28,
        head: [['Date', 'Material', 'Type', 'Direction', 'Qty', 'Location', 'Reference']],
        body: inventoryMovement.items.map(item => [
          item.date, item.material_name, item.transaction_type, item.direction,
          item.quantity, item.location, item.reference_type,
        ]),
      });
    }

    doc.save(`${type}-report-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exported successfully');
  };

  const exportExcel = (type) => {
    let data, filename;

    if (type === 'pending') {
      data = pendingOrders.map(o => ({ 'SO Number': o.so_number, 'Customer': o.customer, 'Product': o.product_name, 'Ordered': o.ordered_qty, 'Supplied': o.supplied_qty, 'Pending': o.pending_qty }));
      filename = 'pending-orders';
    } else if (type === 'goods') {
      data = finishedGoods.map(g => ({ 'Product Code': g.product_code, 'Name': g.name, 'Stock': g.current_stock, 'Unit': g.unit }));
      filename = 'finished-goods-balance';
    } else if (type === 'shortage') {
      data = shortageReport.map(s => ({ 'Code': s.material_code, 'Material': s.material_name, 'Required': s.required_qty, 'Available': s.available_qty, 'Shortage': s.shortage_qty, 'Unit': s.unit, 'Location': s.location, 'Status': s.status }));
      filename = 'raw-material-shortage';
    } else if (type === 'production') {
      data = productionPerformance.items.map(i => ({ 'Type': i.type, 'Reference': i.reference, 'Product': i.product_name, 'Quantity': i.quantity, 'Stage': i.current_stage, 'Status': i.status, 'Created': i.created_at }));
      filename = 'production-performance';
    } else if (type === 'purchase') {
      data = purchaseHistory.items.map(i => ({ 'PO Number': i.po_number, 'Date': i.date, 'Supplier': i.supplier, 'Material Code': i.material_code, 'Material': i.material_name, 'Quantity': i.quantity, 'Unit Price': i.unit_price, 'Total': i.total, 'Status': i.status, 'Location': i.location }));
      filename = 'purchase-order-history';
    } else if (type === 'inventory') {
      data = inventoryMovement.items.map(i => ({ 'Date': i.date, 'Time': i.time, 'Material': i.material_name, 'Type': i.transaction_type, 'Direction': i.direction, 'Quantity': i.quantity, 'Location': i.location, 'Reference': i.reference_type, 'Notes': i.notes }));
      filename = 'inventory-movement';
    }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `${filename}-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exported successfully');
  };

  const shortageCount = shortageReport.filter(s => s.status === 'SHORTAGE').length;

  // Filter Panel Component
  const FilterPanel = ({ showProduct, showMaterial, showTransactionType }) => (
    <div className="bg-stone-50 border border-stone-200 rounded-sm p-4 mb-4" data-testid="filter-panel">
      <div className="flex items-center gap-2 mb-3">
        <Filter size={16} className="text-stone-500" />
        <span className="text-sm font-medium text-stone-700">Filters</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div>
          <label className="text-xs text-stone-500 mb-1 block">From Date</label>
          <Input type="date" value={filters.fromDate} onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })} className="h-8 text-sm" data-testid="filter-from-date" />
        </div>
        <div>
          <label className="text-xs text-stone-500 mb-1 block">To Date</label>
          <Input type="date" value={filters.toDate} onChange={(e) => setFilters({ ...filters, toDate: e.target.value })} className="h-8 text-sm" data-testid="filter-to-date" />
        </div>
        <div>
          <label className="text-xs text-stone-500 mb-1 block">Status</label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="h-8 text-sm" data-testid="filter-status">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="received">Received</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {showProduct && (
          <div>
            <label className="text-xs text-stone-500 mb-1 block">Product</label>
            <Select value={filters.productId || 'all_products'} onValueChange={(v) => setFilters({ ...filters, productId: v === 'all_products' ? '' : v })}>
              <SelectTrigger className="h-8 text-sm w-full" data-testid="filter-product">
                <SelectValue placeholder="All Products" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_products">All Products</SelectItem>
                {filterOptions.products.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {showMaterial && (
          <div>
            <label className="text-xs text-stone-500 mb-1 block">Material</label>
            <Select value={filters.materialId || 'all_materials'} onValueChange={(v) => setFilters({ ...filters, materialId: v === 'all_materials' ? '' : v })}>
              <SelectTrigger className="h-8 text-sm" data-testid="filter-material">
                <SelectValue placeholder="All Materials" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_materials">All Materials</SelectItem>
                {filterOptions.materials.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <label className="text-xs text-stone-500 mb-1 block">Location</label>
          <Select value={filters.location || 'all_locations'} onValueChange={(v) => setFilters({ ...filters, location: v === 'all_locations' ? '' : v })}>
            <SelectTrigger className="h-8 text-sm" data-testid="filter-location">
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_locations">All Locations</SelectItem>
              {filterOptions.locations.map(loc => (
                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {showTransactionType && (
          <div>
            <label className="text-xs text-stone-500 mb-1 block">Transaction Type</label>
            <Select value={filters.transactionType} onValueChange={(v) => setFilters({ ...filters, transactionType: v })}>
              <SelectTrigger className="h-8 text-sm" data-testid="filter-transaction-type">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="in">Inbound</SelectItem>
                <SelectItem value="out">Outbound</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-3">
        <Button size="sm" onClick={handleApplyFilters} data-testid="apply-filters-btn">
          <RefreshCw size={14} className="mr-1" /> Apply Filters
        </Button>
        <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
          Clear
        </Button>
      </div>
    </div>
  );

  // Summary Card Component
  const SummaryCard = ({ label, value, subValue, color = 'stone' }) => (
    <div className={`bg-${color}-50 border border-${color}-200 rounded-sm p-3`}>
      <p className="text-xs text-stone-500 uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
      {subValue && <p className="text-xs text-stone-500">{subValue}</p>}
    </div>
  );

  if (loading && !productionPerformance.items.length && !purchaseHistory.items.length) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="reports-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-bold text-stone-800 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Reports</h1>
          <p className="text-sm text-stone-500 mt-2 font-medium">View, filter, and export business reports.</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'production') fetchProductionPerformance(); else if (v === 'purchase') fetchPurchaseHistory(); else if (v === 'inventory') fetchInventoryMovement(); }} className="space-y-4">
        <TabsList className="bg-stone-100 p-1">
          <TabsTrigger value="shortage" className="data-[state=active]:bg-white">
            <AlertTriangle size={14} className="mr-2" /> Material Shortage
          </TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-white">
            <Package size={14} className="mr-2" /> Pending Orders
          </TabsTrigger>
          <TabsTrigger value="goods" className="data-[state=active]:bg-white">
            <Package size={14} className="mr-2" /> Finished Goods
          </TabsTrigger>
          <TabsTrigger value="production" className="data-[state=active]:bg-white">
            <TrendingUp size={14} className="mr-2" /> Production Performance
          </TabsTrigger>
          <TabsTrigger value="purchase" className="data-[state=active]:bg-white">
            <ShoppingCart size={14} className="mr-2" /> Purchase History
          </TabsTrigger>
          <TabsTrigger value="inventory" className="data-[state=active]:bg-white">
            <ArrowLeftRight size={14} className="mr-2" /> Inventory Movement
          </TabsTrigger>
        </TabsList>

        {/* Raw Material Shortage Report */}
        <TabsContent value="shortage">
          <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm p-6" data-testid="shortage-report">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-stone-800">Raw Material Shortage</h2>
                {shortageCount > 0 && (
                  <span className="flex items-center gap-1 px-2.5 py-1 bg-red-50 border border-red-200 text-red-700 text-xs font-medium rounded-full">
                    <AlertTriangle size={12} className="text-red-500" /> {shortageCount} shortage{shortageCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportPDF('shortage')} data-testid="export-shortage-pdf" className="h-8 text-xs bg-white text-stone-600 border-stone-200 hover:bg-stone-50 hover:text-stone-900">
                  <FileDown size={14} className="mr-2" /> PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportExcel('shortage')} data-testid="export-shortage-excel" className="h-8 text-xs bg-white text-stone-600 border-stone-200 hover:bg-stone-50 hover:text-stone-900">
                  <FileDown size={14} className="mr-2" /> Excel
                </Button>
              </div>
            </div>
            <p className="text-xs text-stone-500 mb-4 font-medium">Based on pending sales orders and BOM requirements</p>
            <div className="overflow-x-auto rounded-lg border border-stone-200/60">
              <table className="w-full">
                <thead className="bg-stone-50/80 border-b border-stone-100/80">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Material</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Required</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Available</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Shortage</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Unit</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {shortageReport.map((item, idx) => (
                    <tr key={idx} className={`hover:bg-stone-50/50 transition-colors ${item.status === 'SHORTAGE' ? 'bg-red-50/30' : ''}`}>
                      <td className="px-6 py-4 text-sm font-medium text-stone-800">{item.material_code}</td>
                      <td className="px-6 py-4 text-sm text-stone-700">{item.material_name}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{item.required_qty.toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{item.available_qty.toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <span className={item.shortage_qty > 0 ? 'text-red-600' : 'text-emerald-600'}>{item.shortage_qty.toFixed(2)}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-500">{item.unit}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{item.location}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${item.status === 'SHORTAGE' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${item.status === 'SHORTAGE' ? 'bg-red-500' : 'bg-emerald-500'}`}></span>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {shortageReport.length === 0 && <p className="text-center py-8 text-stone-500">No material requirements from pending orders</p>}
            </div>
          </div>
        </TabsContent>

        {/* Pending Orders Report */}
        <TabsContent value="pending">
          <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm p-6" data-testid="pending-orders-report">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-stone-800">Pending Orders</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportPDF('pending')} data-testid="export-pending-pdf" className="h-8 text-xs bg-white text-stone-600 border-stone-200 hover:bg-stone-50">
                  <FileDown size={14} className="mr-2" /> PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportExcel('pending')} data-testid="export-pending-excel" className="h-8 text-xs bg-white text-stone-600 border-stone-200 hover:bg-stone-50">
                  <FileDown size={14} className="mr-2" /> Excel
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-stone-200/60">
              <table className="w-full">
                <thead className="bg-stone-50/80 border-b border-stone-100/80">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">SO Number</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Ordered</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Supplied</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Pending</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {pendingOrders.map((order, idx) => (
                    <tr key={idx} className="hover:bg-stone-50/50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-stone-800">{order.so_number}</td>
                      <td className="px-6 py-4 text-sm text-stone-700">{order.customer}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{order.product_name}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{order.ordered_qty}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{order.supplied_qty}</td>
                      <td className="px-6 py-4 text-sm text-amber-600 font-medium">{order.pending_qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pendingOrders.length === 0 && <p className="text-center py-8 text-stone-500">No pending orders</p>}
            </div>
          </div>
        </TabsContent>

        {/* Finished Goods Balance Report */}
        <TabsContent value="goods">
          <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm p-6" data-testid="finished-goods-report">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-stone-800">Finished Goods Balance</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportPDF('goods')} data-testid="export-goods-pdf" className="h-8 text-xs bg-white text-stone-600 border-stone-200 hover:bg-stone-50">
                  <FileDown size={14} className="mr-2" /> PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportExcel('goods')} data-testid="export-goods-excel" className="h-8 text-xs bg-white text-stone-600 border-stone-200 hover:bg-stone-50">
                  <FileDown size={14} className="mr-2" /> Excel
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-stone-200/60">
              <table className="w-full">
                <thead className="bg-stone-50/80 border-b border-stone-100/80">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Product Code</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Current Stock</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Unit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {finishedGoods.map((good) => (
                    <tr key={good.id} className="hover:bg-stone-50/50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-stone-800">{good.product_code}</td>
                      <td className="px-6 py-4 text-sm text-stone-700">{good.name}</td>
                      <td className="px-6 py-4 text-sm font-medium text-stone-800">{good.current_stock}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{good.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Production Performance Report */}
        <TabsContent value="production">
          <FilterPanel showProduct showMaterial={false} showTransactionType={false} />
          <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm p-6" data-testid="production-performance-report">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-stone-800">Production Performance</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportPDF('production')} data-testid="export-production-pdf" className="h-8 text-xs bg-white text-stone-600 border-stone-200 hover:bg-stone-50">
                  <FileDown size={14} className="mr-2" /> PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportExcel('production')} data-testid="export-production-excel" className="h-8 text-xs bg-white text-stone-600 border-stone-200 hover:bg-stone-50">
                  <FileDown size={14} className="mr-2" /> Excel
                </Button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
              <div className="bg-blue-50/60 border border-blue-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">Total Orders</p>
                <p className="text-2xl font-bold text-blue-700 mt-1">{productionPerformance.summary.total_orders || 0}</p>
              </div>
              <div className="bg-emerald-50/60 border border-emerald-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">Completed</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1">{productionPerformance.summary.completed || 0}</p>
              </div>
              <div className="bg-amber-50/60 border border-amber-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">In Progress</p>
                <p className="text-2xl font-bold text-amber-700 mt-1">{productionPerformance.summary.in_progress || 0}</p>
              </div>
              <div className="bg-red-50/60 border border-red-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">Delayed</p>
                <p className="text-2xl font-bold text-red-700 mt-1">{productionPerformance.summary.delayed || 0}</p>
              </div>
              <div className="bg-green-50/60 border border-green-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">Completion Rate</p>
                <p className="text-2xl font-bold text-green-700 mt-1">{productionPerformance.summary.completion_rate || 0}%</p>
              </div>
              <div className="bg-teal-50/60 border border-teal-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">On-Time Rate</p>
                <p className="text-2xl font-bold text-teal-700 mt-1">{productionPerformance.summary.on_time_rate || 0}%</p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-stone-200/60">
              <table className="w-full">
                <thead className="bg-stone-50/80 border-b border-stone-100/80">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Reference</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Current Stage</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {productionPerformance.items.map((item, idx) => (
                    <tr key={idx} className="hover:bg-stone-50/50 transition-colors">
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2.5 py-1 rounded text-xs font-medium ${item.type.includes('Order') ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {item.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-stone-800">{item.reference}</td>
                      <td className="px-6 py-4 text-sm text-stone-700">{item.product_name}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{item.quantity}</td>
                      <td className="px-6 py-4 text-sm capitalize text-stone-600">{item.current_stage}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${item.status === 'Completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          item.status === 'Delayed' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${item.status === 'Completed' ? 'bg-emerald-500' : item.status === 'Delayed' ? 'bg-red-500' : 'bg-amber-500'}`}></span>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {productionPerformance.items.length === 0 && <p className="text-center py-8 text-stone-500">No production data. Apply filters and click Apply.</p>}
            </div>
          </div>
        </TabsContent>

        {/* Purchase Order History Report */}
        <TabsContent value="purchase">
          <FilterPanel showProduct={false} showMaterial showTransactionType={false} />
          <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm p-6" data-testid="purchase-history-report">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-stone-800">Purchase Order History</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportPDF('purchase')} data-testid="export-purchase-pdf" className="h-8 text-xs bg-white text-stone-600 border-stone-200 hover:bg-stone-50">
                  <FileDown size={14} className="mr-2" /> PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportExcel('purchase')} data-testid="export-purchase-excel" className="h-8 text-xs bg-white text-stone-600 border-stone-200 hover:bg-stone-50">
                  <FileDown size={14} className="mr-2" /> Excel
                </Button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-blue-50/60 border border-blue-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">Total Orders</p>
                <p className="text-2xl font-bold text-blue-700 mt-1">{purchaseHistory.summary.total_orders || 0}</p>
              </div>
              <div className="bg-emerald-50/60 border border-emerald-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">Received</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1">{purchaseHistory.summary.received || 0}</p>
              </div>
              <div className="bg-amber-50/60 border border-amber-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">Pending</p>
                <p className="text-2xl font-bold text-amber-700 mt-1">{purchaseHistory.summary.pending || 0}</p>
              </div>
              <div className="bg-indigo-50/60 border border-indigo-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">Total Value</p>
                <p className="text-2xl font-bold text-indigo-700 mt-1">${(purchaseHistory.summary.total_value || 0).toLocaleString()}</p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-stone-200/60">
              <table className="w-full">
                <thead className="bg-stone-50/80 border-b border-stone-100/80">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">PO Number</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Supplier</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Material</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Qty</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {purchaseHistory.items.map((item, idx) => (
                    <tr key={idx} className="hover:bg-stone-50/50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-stone-800">{item.po_number}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{item.date}</td>
                      <td className="px-6 py-4 text-sm text-stone-700">{item.supplier}</td>
                      <td className="px-6 py-4 text-sm text-stone-700">{item.material_name}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{item.quantity} {item.unit}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">${item.unit_price}</td>
                      <td className="px-6 py-4 text-sm font-medium text-stone-800">${item.total.toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${item.status === 'received' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${item.status === 'received' ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {purchaseHistory.items.length === 0 && <p className="text-center py-8 text-stone-500">No purchase history. Apply filters and click Apply.</p>}
            </div>
          </div>
        </TabsContent>

        {/* Inventory Movement Report */}
        <TabsContent value="inventory">
          <FilterPanel showProduct={false} showMaterial showTransactionType />
          <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm p-6" data-testid="inventory-movement-report">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-stone-800">Inventory Movement</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportPDF('inventory')} data-testid="export-inventory-pdf" className="h-8 text-xs bg-white text-stone-600 border-stone-200 hover:bg-stone-50">
                  <FileDown size={14} className="mr-2" /> PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportExcel('inventory')} data-testid="export-inventory-excel" className="h-8 text-xs bg-white text-stone-600 border-stone-200 hover:bg-stone-50">
                  <FileDown size={14} className="mr-2" /> Excel
                </Button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-blue-50/60 border border-blue-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">Total Transactions</p>
                <p className="text-2xl font-bold text-blue-700 mt-1">{inventoryMovement.summary.total_transactions || 0}</p>
              </div>
              <div className="bg-emerald-50/60 border border-emerald-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">Total In</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1">+{inventoryMovement.summary.total_in || 0}</p>
              </div>
              <div className="bg-red-50/60 border border-red-200/60 rounded-xl p-4">
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">Total Out</p>
                <p className="text-2xl font-bold text-red-700 mt-1">-{inventoryMovement.summary.total_out || 0}</p>
              </div>
              <div className={`border rounded-xl p-4 ${(inventoryMovement.summary.net_change || 0) >= 0 ? 'bg-teal-50/60 border-teal-200/60' : 'bg-orange-50/60 border-orange-200/60'}`}>
                <p className="text-xs text-stone-500 uppercase font-medium tracking-wide">Net Change</p>
                <p className={`text-2xl font-bold mt-1 ${(inventoryMovement.summary.net_change || 0) >= 0 ? 'text-teal-700' : 'text-orange-700'}`}>
                  {(inventoryMovement.summary.net_change || 0) >= 0 ? '+' : ''}{inventoryMovement.summary.net_change || 0}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-stone-200/60">
              <table className="w-full">
                <thead className="bg-stone-50/80 border-b border-stone-100/80">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Material</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Direction</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {inventoryMovement.items.map((item, idx) => (
                    <tr key={idx} className="hover:bg-stone-50/50 transition-colors">
                      <td className="px-6 py-4 text-sm text-stone-500">{item.date}</td>
                      <td className="px-6 py-4 text-sm font-medium text-stone-800">{item.material_name}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className="px-2.5 py-1 rounded text-xs bg-stone-100 text-stone-700 font-medium">{item.transaction_type}</span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${item.direction === 'IN' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'
                          }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${item.direction === 'IN' ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                          {item.direction}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-stone-800">{item.quantity}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{item.location}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{item.reference_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {inventoryMovement.items.length === 0 && <p className="text-center py-8 text-stone-500">No inventory movements. Apply filters and click Apply.</p>}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
